/**
 * XeroMappingEditor — Sprint 24 F007
 *
 * Displays Xero raw accounts (Tier 1) alongside auto-suggested category
 * mappings (Tier 2). Users can accept, adjust or override each mapping via
 * dropdown.  Supports:
 *   - Grouped category Select (P&L / BS)
 *   - Custom category labels
 *   - Confidence colour indicators
 *   - Search / tab filtering
 *   - Template save + apply
 *   - Standardised preview (Tier 3)
 *   - Confirm & save to P&L / BS tables
 *
 * Props:
 *   financialPeriodId  — UUID of the financial period
 *   entityRecordId     — UUID of the entity financial record
 *   connectionId       — UUID of the Xero connection (informational)
 *   suggestedMappings  — Initial mapping suggestions from sync route
 *   onComplete         — Callback after successful confirm & save
 *   onBack             — Callback to go back to previous step
 *   onCancel           — Callback to cancel the wizard
 */

import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Save,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ============================================================================
// Types
// ============================================================================

export interface MappingSuggestion {
  xeroAccountCode: string;
  xeroAccountName: string;
  xeroAccountType: string;
  suggestedCategory: string;
  suggestedReportSection: "profit_loss" | "balance_sheet";
  confidence: number;
}

export interface StandardisedResult {
  profitLossItems: number;
  balanceSheetItems: number;
  unmappedAccounts: number;
}

export interface XeroMappingEditorProps {
  financialPeriodId: string;
  entityRecordId: string;
  connectionId: string;
  suggestedMappings: MappingSuggestion[];
  onComplete: (result: StandardisedResult) => void;
  onBack: () => void;
  onCancel: () => void;
}

interface XeroRawAccount {
  id: string;
  financialPeriodId: string;
  xeroAccountId: string;
  accountCode: string | null;
  accountName: string;
  accountType: string;
  xeroClass: string | null;
  balance: string;
  reportType: string | null;
  createdAt: string;
}

interface XeroAccountMapping {
  id: string;
  xeroAccountCode: string;
  xeroAccountName: string | null;
  standardCategory: string;
  reportSection: string;
  customCategoryLabel: string | null;
  isOverride: number;
}

interface MappingEdit {
  standardCategory: string;
  reportSection: string;
  customCategoryLabel: string | null;
  isOverride: boolean;
}

interface ContributingAccount {
  xeroAccountCode: string;
  xeroAccountName: string;
  amount: number;
}

interface StandardisedLineItem {
  lineItem: string;
  amount: number;
  contributingAccounts: ContributingAccount[];
}

interface StandardiseApiResult {
  profitLossItems: number;
  balanceSheetItems: number;
  unmappedAccounts: number;
  unmappedList: Array<{ code: string; name: string; balance: number }>;
}

interface StandardisedPreviewData {
  profitLoss: StandardisedLineItem[];
  balanceSheet: StandardisedLineItem[];
  unmappedAccounts: Array<{ code: string; name: string; balance: number }>;
}

// ============================================================================
// Category definitions (P&L + BS)
// ============================================================================

const PL_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "income_sales", label: "Revenue / Sales" },
  { value: "other_income", label: "Other Income" },
  { value: "cost_of_goods_sold", label: "Cost of Goods Sold" },
  { value: "wages_salaries", label: "Wages and Salaries" },
  { value: "rent_premises", label: "Rent" },
  { value: "utilities", label: "Utilities" },
  { value: "insurance", label: "Insurance" },
  { value: "marketing", label: "Marketing" },
  { value: "professional_fees", label: "Professional Fees" },
  { value: "motor_vehicle_expenses", label: "Motor Vehicle" },
  { value: "office_expenses", label: "Office Expenses" },
  { value: "other_operating_expenses", label: "Other Operating Expenses" },
  { value: "depreciation", label: "Depreciation" },
  { value: "amortisation", label: "Amortisation" },
  { value: "interest_expense", label: "Interest Expense" },
  { value: "lease_expense", label: "Lease Expense" },
  { value: "tax_expense", label: "Tax Expense" },
];

const BS_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "cash_business_account", label: "Cash and Cash Equivalents" },
  { value: "accounts_receivable", label: "Accounts Receivable" },
  { value: "inventory", label: "Inventory" },
  { value: "prepayments", label: "Prepayments" },
  { value: "other_current_assets", label: "Other Current Assets" },
  { value: "property_plant_equipment", label: "Property, Plant and Equipment" },
  { value: "investments", label: "Investments" },
  { value: "intangibles", label: "Intangibles" },
  { value: "goodwill", label: "Goodwill" },
  { value: "other_non_current_assets", label: "Other Non-Current Assets" },
  { value: "accounts_payable", label: "Accounts Payable" },
  { value: "short_term_debt", label: "Short Term Debt" },
  { value: "tax_payable", label: "Tax Payable" },
  { value: "provisions", label: "Provisions" },
  { value: "other_current_liabilities", label: "Other Current Liabilities" },
  { value: "long_term_debt", label: "Long Term Debt" },
  { value: "deferred_tax_liabilities", label: "Deferred Tax Liabilities" },
  { value: "other_non_current_liabilities", label: "Other Non-Current Liabilities" },
  { value: "share_capital", label: "Share Capital" },
  { value: "retained_earnings", label: "Retained Earnings" },
  { value: "reserves", label: "Reserves" },
  { value: "other_equity", label: "Other Equity" },
];

function isPlCategory(category: string): boolean {
  return PL_CATEGORIES.some((c) => c.value === category);
}

function getCategoryLabel(category: string): string {
  const pl = PL_CATEGORIES.find((c) => c.value === category);
  if (pl) return pl.label;
  const bs = BS_CATEGORIES.find((c) => c.value === category);
  if (bs) return bs.label;
  return category;
}

// ============================================================================
// Helpers
// ============================================================================

function formatCurrency(amount: number | string): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function getConfidenceBadgeClass(confidence: number): string {
  if (confidence >= 0.8) return "bg-green-100 text-green-800";
  if (confidence >= 0.5) return "bg-amber-100 text-amber-800";
  return "bg-red-100 text-red-800";
}

function getXeroTypeReportSection(accountType: string): "profit_loss" | "balance_sheet" | null {
  const plTypes = new Set(["REVENUE", "SALES", "OTHERINCOME", "DIRECTCOSTS", "EXPENSE", "OVERHEADS", "DEPRECIATN", "SUPERANNUATIONEXPENSE"]);
  const bsTypes = new Set(["BANK", "CURRENT", "FIXED", "NONCURRENT", "CURRLIAB", "LIABILITY", "TERMLIAB", "EQUITY", "INVENTORY", "PREPAYMENT", "PAYGLIABILITY", "SUPERANNUATIONLIABILITY", "WABORESPAYABLE"]);
  if (plTypes.has(accountType)) return "profit_loss";
  if (bsTypes.has(accountType)) return "balance_sheet";
  return null;
}

// ============================================================================
// Category Select (grouped P&L + BS)
// ============================================================================

function CategorySelect({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  testId: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-8 text-xs w-full min-w-[180px]"
        data-testid={testId}
      >
        <SelectValue placeholder="Select category..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unmapped" className="text-xs text-muted-foreground">
          — Unmapped —
        </SelectItem>
        <SelectGroup>
          <SelectLabel className="text-xs font-semibold text-muted-foreground">
            P&amp;L Accounts
          </SelectLabel>
          {PL_CATEGORIES.map((cat) => (
            <SelectItem key={cat.value} value={cat.value} className="text-xs">
              {cat.label}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel className="text-xs font-semibold text-muted-foreground">
            Balance Sheet
          </SelectLabel>
          {BS_CATEGORIES.map((cat) => (
            <SelectItem key={cat.value} value={cat.value} className="text-xs">
              {cat.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

// ============================================================================
// Standardised Preview
// ============================================================================

function StandardisedPreview({
  data,
  onConfirm,
  onBack,
  isSaving,
}: {
  data: StandardisedPreviewData;
  onConfirm: () => void;
  onBack: () => void;
  isSaving: boolean;
}) {
  const [expandedPl, setExpandedPl] = useState<Set<string>>(new Set());
  const [expandedBs, setExpandedBs] = useState<Set<string>>(new Set());

  function togglePlExpand(lineItem: string) {
    setExpandedPl((prev) => {
      const next = new Set(prev);
      if (next.has(lineItem)) next.delete(lineItem);
      else next.add(lineItem);
      return next;
    });
  }

  function toggleBsExpand(lineItem: string) {
    setExpandedBs((prev) => {
      const next = new Set(prev);
      if (next.has(lineItem)) next.delete(lineItem);
      else next.add(lineItem);
      return next;
    });
  }

  const plTotal = data.profitLoss.reduce((sum, item) => sum + item.amount, 0);
  const bsTotal = data.balanceSheet.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div data-testid="standardised-preview" className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* P&L Preview */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-semibold">
              Profit &amp; Loss
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.profitLoss.map((item) => (
                  <>
                    <TableRow
                      key={item.lineItem}
                      data-testid={`preview-pl-${item.lineItem}`}
                    >
                      <TableCell className="text-xs py-2">
                        {getCategoryLabel(item.lineItem)}
                      </TableCell>
                      <TableCell className="text-xs py-2 text-right">
                        {formatCurrency(item.amount)}
                      </TableCell>
                      <TableCell className="py-2">
                        {item.contributingAccounts.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => togglePlExpand(item.lineItem)}
                          >
                            {expandedPl.has(item.lineItem) ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedPl.has(item.lineItem) &&
                      item.contributingAccounts.map((acc) => (
                        <TableRow
                          key={acc.xeroAccountCode}
                          data-testid={`preview-pl-${item.lineItem}-acc-${acc.xeroAccountCode}`}
                          className="bg-muted/40"
                        >
                          <TableCell className="text-xs py-1 pl-6 text-muted-foreground">
                            {acc.xeroAccountCode} — {acc.xeroAccountName}
                          </TableCell>
                          <TableCell className="text-xs py-1 text-right text-muted-foreground">
                            {formatCurrency(acc.amount)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      ))}
                  </>
                ))}
                {data.profitLoss.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-xs text-muted-foreground text-center py-4"
                    >
                      No P&amp;L items
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell className="text-xs py-2">Total</TableCell>
                  <TableCell className="text-xs py-2 text-right">
                    {formatCurrency(plTotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* BS Preview */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-semibold">
              Balance Sheet
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.balanceSheet.map((item) => (
                  <>
                    <TableRow
                      key={item.lineItem}
                      data-testid={`preview-bs-${item.lineItem}`}
                    >
                      <TableCell className="text-xs py-2">
                        {getCategoryLabel(item.lineItem)}
                      </TableCell>
                      <TableCell className="text-xs py-2 text-right">
                        {formatCurrency(item.amount)}
                      </TableCell>
                      <TableCell className="py-2">
                        {item.contributingAccounts.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => toggleBsExpand(item.lineItem)}
                          >
                            {expandedBs.has(item.lineItem) ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedBs.has(item.lineItem) &&
                      item.contributingAccounts.map((acc) => (
                        <TableRow
                          key={acc.xeroAccountCode}
                          data-testid={`preview-bs-${item.lineItem}-acc-${acc.xeroAccountCode}`}
                          className="bg-muted/40"
                        >
                          <TableCell className="text-xs py-1 pl-6 text-muted-foreground">
                            {acc.xeroAccountCode} — {acc.xeroAccountName}
                          </TableCell>
                          <TableCell className="text-xs py-1 text-right text-muted-foreground">
                            {formatCurrency(acc.amount)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      ))}
                  </>
                ))}
                {data.balanceSheet.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-xs text-muted-foreground text-center py-4"
                    >
                      No Balance Sheet items
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell className="text-xs py-2">Total</TableCell>
                  <TableCell className="text-xs py-2 text-right">
                    {formatCurrency(bsTotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Unmapped warning */}
      {data.unmappedAccounts.length > 0 && (
        <div
          data-testid="preview-unmapped-warning"
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <strong>{data.unmappedAccounts.length} account
            {data.unmappedAccounts.length === 1 ? "" : "s"} not
            included</strong>{" "}
            — unmapped accounts are excluded from the standardised financials.
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack} disabled={isSaving}>
          Back to Mapping
        </Button>
        <Button
          data-testid="btn-confirm-save"
          onClick={onConfirm}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving standardised financials...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Confirm &amp; Save
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

type TabFilter = "all" | "pl" | "bs" | "unmapped";

export function XeroMappingEditor({
  financialPeriodId,
  entityRecordId,
  suggestedMappings,
  onComplete,
  onBack,
  onCancel,
}: XeroMappingEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ---- Fetch raw accounts (Tier 1) ----
  const { data: rawAccounts = [], isLoading: isLoadingAccounts } = useQuery<
    XeroRawAccount[]
  >({
    queryKey: [`/api/xero/raw-accounts?financialPeriodId=${financialPeriodId}`],
    enabled: !!financialPeriodId,
  });

  // ---- Fetch existing mappings (Tier 2) ----
  const { data: savedMappings = [] } = useQuery<XeroAccountMapping[]>({
    queryKey: [`/api/xero/mappings?entityRecordId=${entityRecordId}`],
    enabled: !!entityRecordId,
  });

  // ---- Local mapping edits: Map<accountCode, MappingEdit> ----
  const [mappingEdits, setMappingEdits] = useState<Map<string, MappingEdit>>(
    new Map(),
  );

  // Populate initial mapping edits when data is loaded
  useEffect(() => {
    if (rawAccounts.length === 0) return;

    const initial = new Map<string, MappingEdit>();

    rawAccounts.forEach((acc) => {
      const code = acc.accountCode ?? acc.xeroAccountId;

      // Check for saved mapping first
      const saved = savedMappings.find((m) => m.xeroAccountCode === code);
      if (saved) {
        initial.set(code, {
          standardCategory: saved.standardCategory,
          reportSection: saved.reportSection,
          customCategoryLabel: saved.customCategoryLabel,
          isOverride: saved.isOverride === 1,
        });
        return;
      }

      // Fall back to suggested mapping
      const suggestion = suggestedMappings.find(
        (s) => s.xeroAccountCode === code,
      );
      if (suggestion && suggestion.confidence >= 0.5) {
        initial.set(code, {
          standardCategory: suggestion.suggestedCategory,
          reportSection: suggestion.suggestedReportSection,
          customCategoryLabel: null,
          isOverride: false,
        });
        return;
      }

      // Default to unmapped
      const inferredSection = getXeroTypeReportSection(acc.accountType);
      initial.set(code, {
        standardCategory: "unmapped",
        reportSection: inferredSection ?? "profit_loss",
        customCategoryLabel: null,
        isOverride: false,
      });
    });

    setMappingEdits(initial);
  }, [rawAccounts, savedMappings, suggestedMappings]);

  // ---- UI state ----
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TabFilter>("all");
  const [previewData, setPreviewData] =
    useState<StandardisedPreviewData | null>(null);

  // ---- Derived: unmapped count ----
  const unmappedCount = useMemo(() => {
    let count = 0;
    rawAccounts.forEach((acc) => {
      const code = acc.accountCode ?? acc.xeroAccountId;
      const edit = mappingEdits.get(code);
      if (!edit || edit.standardCategory === "unmapped") count++;
    });
    return count;
  }, [rawAccounts, mappingEdits]);

  // ---- Derived: filtered accounts ----
  const filteredAccounts = useMemo(() => {
    return rawAccounts.filter((acc) => {
      const code = acc.accountCode ?? acc.xeroAccountId;
      const edit = mappingEdits.get(code);

      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !acc.accountName.toLowerCase().includes(q) &&
          !(acc.accountCode ?? "").toLowerCase().includes(q)
        ) {
          return false;
        }
      }

      // Tab filter
      if (activeTab === "pl") {
        if (!edit || edit.standardCategory === "unmapped") return false;
        return isPlCategory(edit.standardCategory);
      }
      if (activeTab === "bs") {
        if (!edit || edit.standardCategory === "unmapped") return false;
        return !isPlCategory(edit.standardCategory);
      }
      if (activeTab === "unmapped") {
        return !edit || edit.standardCategory === "unmapped";
      }

      return true;
    });
  }, [rawAccounts, mappingEdits, searchQuery, activeTab]);

  // ---- Get confidence for an account ----
  function getConfidence(accountCode: string): number | null {
    const suggestion = suggestedMappings.find(
      (s) => s.xeroAccountCode === accountCode,
    );
    return suggestion ? suggestion.confidence : null;
  }

  // ---- Handle mapping change ----
  function handleMappingChange(accountCode: string, newCategory: string) {
    setMappingEdits((prev) => {
      const next = new Map(prev);
      const current = next.get(accountCode) ?? {
        standardCategory: "unmapped",
        reportSection: "profit_loss",
        customCategoryLabel: null,
        isOverride: false,
      };
      const newSection = isPlCategory(newCategory)
        ? "profit_loss"
        : "balance_sheet";
      next.set(accountCode, {
        ...current,
        standardCategory: newCategory,
        reportSection: newSection,
        isOverride: true,
      });
      return next;
    });
    // Clear preview when mapping changes
    setPreviewData(null);
  }

  // ---- Save mappings mutation ----
  const saveMappingsMutation = useMutation({
    mutationFn: async () => {
      const mappings: Array<{
        xeroAccountCode: string;
        xeroAccountName?: string;
        standardCategory: string;
        reportSection: string;
        customCategoryLabel?: string | null;
        isOverride: boolean;
      }> = [];

      rawAccounts.forEach((acc) => {
        const code = acc.accountCode ?? acc.xeroAccountId;
        const edit = mappingEdits.get(code);
        if (!edit || edit.standardCategory === "unmapped") return;
        mappings.push({
          xeroAccountCode: code,
          xeroAccountName: acc.accountName,
          standardCategory: edit.standardCategory,
          reportSection: edit.reportSection,
          customCategoryLabel: edit.customCategoryLabel,
          isOverride: edit.isOverride,
        });
      });

      const res = await apiRequest("PUT", "/api/xero/mappings", {
        entityRecordId,
        mappings,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/xero"] });
      toast({ title: "Mappings saved", description: "Account mappings updated." });
    },
    onError: (error: Error) => {
      toast({
        title: "Save failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ---- Generate preview mutation ----
  const generatePreviewMutation = useMutation({
    mutationFn: async () => {
      // Save mappings first, then standardise
      const mappings: Array<{
        xeroAccountCode: string;
        xeroAccountName?: string;
        standardCategory: string;
        reportSection: string;
        isOverride: boolean;
      }> = [];

      rawAccounts.forEach((acc) => {
        const code = acc.accountCode ?? acc.xeroAccountId;
        const edit = mappingEdits.get(code);
        if (!edit || edit.standardCategory === "unmapped") return;
        mappings.push({
          xeroAccountCode: code,
          xeroAccountName: acc.accountName,
          standardCategory: edit.standardCategory,
          reportSection: edit.reportSection,
          isOverride: edit.isOverride,
        });
      });

      await apiRequest("PUT", "/api/xero/mappings", {
        entityRecordId,
        mappings,
      });

      const res = await apiRequest("POST", "/api/xero/standardise", {
        financialPeriodId,
        entityRecordId,
      });
      const data: StandardiseApiResult = await res.json();

      // Build a preview from the mapped accounts
      const plMap = new Map<
        string,
        { amount: number; contributing: ContributingAccount[] }
      >();
      const bsMap = new Map<
        string,
        { amount: number; contributing: ContributingAccount[] }
      >();

      rawAccounts.forEach((acc) => {
        const code = acc.accountCode ?? acc.xeroAccountId;
        const edit = mappingEdits.get(code);
        if (!edit || edit.standardCategory === "unmapped") return;
        const balance = parseFloat(acc.balance) || 0;
        const contrib: ContributingAccount = {
          xeroAccountCode: code,
          xeroAccountName: acc.accountName,
          amount: balance,
        };
        if (edit.reportSection === "profit_loss") {
          const existing = plMap.get(edit.standardCategory) ?? {
            amount: 0,
            contributing: [],
          };
          plMap.set(edit.standardCategory, {
            amount: existing.amount + balance,
            contributing: [...existing.contributing, contrib],
          });
        } else {
          const existing = bsMap.get(edit.standardCategory) ?? {
            amount: 0,
            contributing: [],
          };
          bsMap.set(edit.standardCategory, {
            amount: existing.amount + balance,
            contributing: [...existing.contributing, contrib],
          });
        }
      });

      const profitLoss: StandardisedLineItem[] = Array.from(plMap.entries()).map(
        ([lineItem, { amount, contributing }]) => ({
          lineItem,
          amount,
          contributingAccounts: contributing,
        }),
      );
      const balanceSheet: StandardisedLineItem[] = Array.from(
        bsMap.entries(),
      ).map(([lineItem, { amount, contributing }]) => ({
        lineItem,
        amount,
        contributingAccounts: contributing,
      }));

      return {
        profitLoss,
        balanceSheet,
        unmappedAccounts: data.unmappedList,
      } as StandardisedPreviewData;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      queryClient.invalidateQueries({ queryKey: ["/api/xero"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Preview failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ---- Confirm & save mutation ----
  const confirmMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/xero/standardise", {
        financialPeriodId,
        entityRecordId,
      });
      const data: StandardiseApiResult = await res.json();
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Financial data saved from Xero",
        description: `Imported ${data.profitLossItems} P&L and ${data.balanceSheetItems} Balance Sheet items.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/xero"] });
      onComplete({
        profitLossItems: data.profitLossItems,
        balanceSheetItems: data.balanceSheetItems,
        unmappedAccounts: data.unmappedAccounts,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Save failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ---- Save template mutation ----
  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      // Save current mappings first
      const mappings: Array<{
        xeroAccountCode: string;
        xeroAccountName?: string;
        standardCategory: string;
        reportSection: string;
        isOverride: boolean;
      }> = [];
      rawAccounts.forEach((acc) => {
        const code = acc.accountCode ?? acc.xeroAccountId;
        const edit = mappingEdits.get(code);
        if (!edit || edit.standardCategory === "unmapped") return;
        mappings.push({
          xeroAccountCode: code,
          xeroAccountName: acc.accountName,
          standardCategory: edit.standardCategory,
          reportSection: edit.reportSection,
          isOverride: edit.isOverride,
        });
      });
      await apiRequest("PUT", "/api/xero/mappings", {
        entityRecordId,
        mappings,
      });
      const res = await apiRequest(
        "POST",
        "/api/xero/mappings/save-template",
        { entityRecordId },
      );
      return res.json() as Promise<{ templateCount: number }>;
    },
    onSuccess: (data) => {
      toast({
        title: "Template saved",
        description: `${data.templateCount} mapping${data.templateCount === 1 ? "" : "s"} saved as reusable template.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Template save failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ---- Loading state ----
  if (isLoadingAccounts) {
    return (
      <div
        data-testid="xero-mapping-editor"
        className="flex flex-col items-center justify-center py-12 gap-3"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Loading Xero account data...
        </p>
      </div>
    );
  }

  // ---- Preview state ----
  if (previewData) {
    return (
      <div data-testid="xero-mapping-editor" className="space-y-4">
        <h3 className="text-base font-semibold">
          Standardised Financial Preview
        </h3>
        <StandardisedPreview
          data={previewData}
          onConfirm={() => confirmMutation.mutate()}
          onBack={() => setPreviewData(null)}
          isSaving={confirmMutation.isPending}
        />
      </div>
    );
  }

  // ---- Mapping Table state ----
  return (
    <div data-testid="xero-mapping-editor" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Showing{" "}
            <strong>{filteredAccounts.length}</strong> of{" "}
            <strong>{rawAccounts.length}</strong> accounts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="btn-save-template"
            variant="outline"
            size="sm"
            onClick={() => saveTemplateMutation.mutate()}
            disabled={saveTemplateMutation.isPending}
          >
            {saveTemplateMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Save className="h-3 w-3 mr-1" />
            )}
            Save as Template
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => saveMappingsMutation.mutate()}
            disabled={saveMappingsMutation.isPending}
          >
            {saveMappingsMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : null}
            Save Mappings
          </Button>
          <Button
            data-testid="btn-generate-preview"
            size="sm"
            onClick={() => generatePreviewMutation.mutate()}
            disabled={generatePreviewMutation.isPending}
          >
            {generatePreviewMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Generate Preview
          </Button>
        </div>
      </div>

      {/* Unmapped warning banner */}
      {unmappedCount > 0 && (
        <div
          data-testid="unmapped-warning-banner"
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong>{unmappedCount} account{unmappedCount === 1 ? "" : "s"} are unmapped</strong>{" "}
            and will not be included in standardised financials.
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-amber-800 h-7"
            onClick={() => setActiveTab("unmapped")}
          >
            Review Unmapped
          </Button>
        </div>
      )}

      {/* Search + Tabs */}
      <div className="flex items-center gap-3">
        <Input
          data-testid="search-accounts"
          placeholder="Search by account name or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-8 text-sm max-w-xs"
        />
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabFilter)}
        >
          <TabsList className="h-8">
            <TabsTrigger
              data-testid="tab-all"
              value="all"
              className="text-xs h-7"
            >
              All
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-pl"
              value="pl"
              className="text-xs h-7"
            >
              P&amp;L Accounts
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-bs"
              value="bs"
              className="text-xs h-7"
            >
              BS Accounts
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-unmapped"
              value="unmapped"
              className="text-xs h-7"
            >
              Unmapped
              {unmappedCount > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1 h-4 text-xs px-1 bg-amber-100 text-amber-700"
                >
                  {unmappedCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Mapping Table */}
      <div data-testid="mapping-table" className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs w-20">Code</TableHead>
              <TableHead className="text-xs">Account Name</TableHead>
              <TableHead className="text-xs w-28">Type</TableHead>
              <TableHead className="text-xs text-right w-28">Amount</TableHead>
              <TableHead className="text-xs w-52">→ Category</TableHead>
              <TableHead className="text-xs w-12 text-center">Conf</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAccounts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-xs text-muted-foreground text-center py-8"
                >
                  {rawAccounts.length === 0
                    ? "No Xero accounts found for this period."
                    : "No accounts match the current filter."}
                </TableCell>
              </TableRow>
            ) : (
              filteredAccounts.map((acc) => {
                const code = acc.accountCode ?? acc.xeroAccountId;
                const edit = mappingEdits.get(code);
                const currentCategory = edit?.standardCategory ?? "unmapped";
                const confidence = getConfidence(code);
                const suggestion = suggestedMappings.find(
                  (s) => s.xeroAccountCode === code,
                );
                const isOverride = edit?.isOverride ?? false;

                return (
                  <TableRow
                    key={acc.id}
                    data-testid={`row-mapping-${code}`}
                    className={
                      currentCategory === "unmapped"
                        ? "bg-amber-50/40"
                        : undefined
                    }
                  >
                    <TableCell className="text-xs font-mono py-2">
                      {code}
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      {acc.accountName}
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="outline" className="text-xs font-normal">
                        {acc.accountType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs py-2 text-right font-mono">
                      {formatCurrency(acc.balance)}
                    </TableCell>
                    <TableCell className="py-2">
                      <CategorySelect
                        value={currentCategory}
                        onChange={(val) => handleMappingChange(code, val)}
                        testId={`select-category-${code}`}
                      />
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      {confidence !== null ? (
                        <span
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${getConfidenceBadgeClass(confidence)}`}
                          title={`${Math.round(confidence * 100)}% confidence${isOverride ? " (overridden)" : ""}${suggestion ? ` — suggested: ${suggestion.suggestedCategory}` : ""}`}
                        >
                          {isOverride ? "↑" : ""}
                          {Math.round(confidence * 100)}%
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-2 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={() => generatePreviewMutation.mutate()}
            disabled={generatePreviewMutation.isPending}
          >
            {generatePreviewMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Generate Preview
          </Button>
        </div>
      </div>
    </div>
  );
}
